import {
  type Note,
  type NoteSummary,
  previewOf,
  type Repository,
} from '@/app/db/repo';
import {
  liveIndex,
  search,
} from '@/app/search/search';
import classNames from 'classnames';
import {
  ChevronLeft,
  ChevronRight,
  Folder,
  FolderOpen,
  SquarePen,
} from 'lucide-react';
import {
  type CSSProperties,
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type EditorHandle,
  NoteEditor,
} from './editor/NoteEditor';
import { isMobile } from './mobile';
import {
  NoteMenu,
  type NoteMenuEditor,
} from './NoteMenu';
import { SearchFilter } from './SearchFilter';
import { SettingsMenu } from './SettingsMenu';
import {
  SidebarResizer,
  useSidebarWidth,
} from './SidebarResizer';
import {
  type Leave,
  useBackgroundSync,
} from './useBackgroundSync';
import { useBackToList } from './useBackToList';
import { useExpandedFolders } from './useExpandedFolders';
import {
  type RunSync,
  type SyncStatus,
  useSync,
} from './useSync';
import { useUpdateReady } from './useUpdateReady';

const SAVE_AFTER_MS = 400;
// how often the list takes in what a sync has pulled so far
const REFRESH_WHILE_SYNCING_MS = 1000;

// a local setting, never synced
const LAST_NOTE = 'lastNote';

type Draft = { title: string, body: string };

// a note without a title - including every note from before titles - is
// listed by its first line, found in the start of the body
export const titleOf = (
  { title, preview }: { title: string, preview: string },
) =>
  title.trim()
  || preview.split('\n').map(l => l.replace(/^#+\s*/, '').trim()).find(Boolean)
  || 'Untitled';

const EMPTY: Draft = { title: '', body: '' };

const draftOf = ({ title, body }: Note): Draft => ({ title, body });

const sameDraft = (a: Draft, b: Draft) =>
  a.title === b.title && a.body === b.body;

export const Notes = (
  { repo, sync, leave, onSignOut }: {
    repo: Repository,
    sync: RunSync,
    leave: Leave,
    onSignOut: () => void,
  },
) => {
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  // the draft as last typed, for code that runs between renders
  const latest = useRef(draft);
  latest.current = draft;
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useSidebarWidth();
  // what the database holds for the open note, to tell a pending edit apart
  // from a note sync has just replaced underneath it
  const saved = useRef<{ id: string, draft: Draft } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingSave = useRef<(() => Promise<void>) | null>(null);
  const bodyRef = useRef<EditorHandle>(null);
  const [searchIndex] = useState(liveIndex);
  const [searchFocused, setSearchFocused] = useState(false);
  // bumped when the index has taken in the notes again
  const [indexed, setIndexed] = useState(0);
  // the notes the last sync couldn't read from the remote
  const [unreadable, setUnreadable] = useState<string[]>([]);
  const [expanded, setExpanded] = useExpandedFolders();
  // the note being dragged, and where it would land: a folder's id, or null
  // for out of any folder
  const dragged = useRef<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null | undefined>();

  const refresh = useCallback(async () => {
    const list = await repo.listNotes();
    setNotes(list);
    return list;
  }, [repo]);

  const allNotes = useCallback(() => repo.allNotes(), [repo]);

  const flush = useCallback(async () => {
    clearTimeout(saveTimer.current);
    const save = pendingSave.current;
    pendingSave.current = null;
    if (save) await save();
  }, []);

  const { status, syncNow, syncSoon } = useSync(sync, {
    beforeSync: flush,
    afterSync: async ({ pushed, pulled, removed, conflicts, unreadable }) => {
      setUnreadable(unreadable);
      if (!pulled && !removed && !conflicts) {
        // a push only clears what's shown as unsynced
        if (pushed) await refresh();
        return;
      }
      await refresh();
      const open = saved.current;
      if (!open) return;
      const note = await repo.getNote(open.id);
      if (!note) {
        // deleted elsewhere while being typed into: saving would update a row
        // that is gone, so what was typed goes on as a new note
        const typed = pendingSave.current !== null
          || !sameDraft(latest.current, open.draft);
        pendingSave.current = null;
        show(typed ? await repo.createNote(latest.current) : null);
        if (typed) {
          await refresh();
          syncSoon();
        }
      } else if (
        !sameDraft(draftOf(note), open.draft)
        // only if nothing was typed since - flush() ran before the sync, so
        // a difference here came from the remote. Typing since is saved over
        // it, keeping the remote's version as a copy (repo.updateNote)
        && !pendingSave.current && sameDraft(latest.current, open.draft)
      ) {
        setDraft(draftOf(note));
        latest.current = draftOf(note);
        saved.current = { id: note.id, draft: draftOf(note) };
      }
    },
  });

  // Notes show up in the list as they're pulled, not once the whole sync is
  // done - on a first sync, minutes later. The sync's end refreshes it again.
  const synced = status.state === 'syncing' ? status.progress?.done ?? 0 : 0;
  const lastRefresh = useRef(0);
  useEffect(() => {
    if (
      !synced || Date.now() - lastRefresh.current < REFRESH_WHILE_SYNCING_MS
    ) {
      return;
    }
    lastRefresh.current = Date.now();
    refresh();
  }, [synced, refresh]);

  // reopens the note that was open last, if it's still there
  useEffect(() => {
    (async () => {
      const [, last] = await Promise.all([
        refresh(),
        repo.getSetting(LAST_NOTE),
      ]);
      const note = last ? await repo.getNote(last) : null;
      if (note) show(note);
    })();
  }, [refresh, repo]);

  // opens a note as read from the database, or closes the one open
  const show = (note: Note | null) => {
    flush();
    if (note) repo.setSetting(LAST_NOTE, note.id);
    else repo.deleteSetting(LAST_NOTE);
    setSelectedId(note?.id ?? null);
    setDraft(note ? draftOf(note) : EMPTY);
    latest.current = note ? draftOf(note) : EMPTY;
    saved.current = note && { id: note.id, draft: draftOf(note) };
  };

  // the open note's text is in the draft, so it's left alone
  const select = async (id: string) => {
    if (id === selectedId) return;
    await flush();
    show(await repo.getNote(id));
  };

  const edit = (id: string, patch: Partial<Draft>) => {
    const next = { ...latest.current, ...patch };
    setDraft(next);
    latest.current = next;
    pendingSave.current = async () => {
      // read before the first await: show() replaces it right after flush()
      const base = saved.current?.id === id ? saved.current.draft : undefined;
      await repo.updateNote(id, next, base);
      saved.current = { id, draft: next };
      await refresh();
      syncSoon();
    };
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_AFTER_MS);
  };

  const create = async () => {
    await flush();
    const note = await repo.createNote();
    await refresh();
    show(note);
  };

  // opened, so the folder just made is there to drop notes into
  const createFolder = async () => {
    const name = prompt('Folder name')?.trim();
    if (!name) return;
    await flush();
    const folder = await repo.createFolder(name);
    setExpanded(folder.id, true);
    await refresh();
    syncSoon();
  };

  const moveToFolder = async (id: string, folderId: string | null) => {
    const note = notes.find(n => n.id === id);
    if (!note || note.folderId === folderId) return;
    await flush();
    await repo.setFolder(id, folderId);
    await refresh();
    syncSoon();
  };

  // what a row being dragged over, or dropped on, does: the note goes into
  // `folderId`, or out of any folder with null
  const dropInto = (folderId: string | null) => ({
    onDragOver: (e: DragEvent) => {
      if (!dragged.current) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDropTarget(folderId);
    },
    onDrop: (e: DragEvent) => {
      const id = dragged.current;
      if (!id) return;
      e.preventDefault();
      e.stopPropagation();
      dragged.current = null;
      setDropTarget(undefined);
      moveToFolder(id, folderId);
    },
  });

  const togglePin = async (note: NoteSummary) => {
    await flush();
    await repo.setPinned(note.id, !note.pinned);
    await refresh();
    syncSoon();
  };

  // an archived note leaves the list, so it closes too unless archived notes
  // are showing
  const toggleArchived = async (note: NoteSummary) => {
    await flush();
    await repo.setArchived(note.id, !note.archived);
    if (!note.archived && !showArchived && note.id === selectedId) {
      show(null);
    }
    await refresh();
    syncSoon();
  };

  const setWidth = async (note: NoteSummary, width: number) => {
    await flush();
    await repo.setWidth(note.id, width);
    await refresh();
    syncSoon();
  };

  // a deleted note goes to the trash, leaving the list, so it closes too
  // unless deleted notes are showing
  const setTrashed = async (note: NoteSummary, trashed: boolean) => {
    await flush();
    await repo.setTrashed(note.id, trashed);
    if (trashed && !showDeleted && note.id === selectedId) show(null);
    await refresh();
    syncSoon();
  };

  // for good, from the trash
  const remove = async (note: NoteSummary) => {
    if (!confirm(`Delete "${titleOf(note)}" forever?`)) return;
    // an edit to the deleted note is dropped, one to another note is kept
    const open = note.id === selectedId;
    if (open) pendingSave.current = null;
    else await flush();
    await repo.deleteNote(note.id);
    if (open) show(null);
    await refresh();
    syncSoon();
  };

  // leaving the app, the last keystrokes are saved first: before the tab
  // goes, and before what's unpushed is handed to the service worker
  const leaveSaved = useCallback(async () => {
    await flush();
    return leave();
  }, [flush, leave]);
  useBackgroundSync(leaveSaved);
  useEffect(() => {
    const onPageHide = () => flush();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [flush]);

  const updateReady = useUpdateReady();

  const selected = notes.find(n => n.id === selectedId) ?? null;
  useBackToList(selected !== null, () => show(null));

  const menuFor = (note: NoteSummary, editor?: NoteMenuEditor) => (
    <NoteMenu
      note={note}
      editor={editor}
      onTogglePin={() => togglePin(note)}
      onToggleArchived={() => toggleArchived(note)}
      onWidth={width => setWidth(note, width)}
      onTrash={() => setTrashed(note, true)}
      onRestore={() => setTrashed(note, false)}
      onDelete={() => remove(note)} />
  );

  const folders = useMemo(() => notes.filter(n => n.isFolder), [notes]);
  const listed = useMemo(
    () =>
      notes.filter(n =>
        !n.isFolder
        && (n.trashed ? showDeleted : showArchived || !n.archived)
      ),
    [notes, showArchived, showDeleted],
  );
  // search is rarely used, so the bodies are read and indexed only while the
  // search field has focus - the first focus indexes everything, later ones
  // just what changed in between. Away from the field, results come from the
  // index as it was.
  useEffect(() => {
    if (!searchFocused) return;
    let wanted = true;
    repo.searchables().then(all => {
      if (!wanted) return;
      searchIndex.update(all);
      setIndexed(n => n + 1);
    });
    return () => {
      wanted = false;
    };
  }, [repo, searchIndex, searchFocused, notes]);
  const shown = useMemo(() => {
    const index = searchIndex.current();
    if (!index || !query.trim()) return listed;
    const byId = new Map(listed.map(n => [n.id, n]));
    return search(index, query).flatMap(id => byId.get(id) ?? []);
    // the index updates in place; `indexed` says when
  }, [searchIndex, listed, query, indexed]);
  const searching = Boolean(query.trim());
  // Out of a search, the notes in no folder come first, then each folder
  // with its notes. One in a folder deleted elsewhere is in none. A search
  // lists what it found, folders or not.
  const inFolder = useMemo(() => {
    const ids = new Set(folders.map(f => f.id));
    return Map.groupBy(
      shown,
      n => n.folderId && ids.has(n.folderId) ? n.folderId : null,
    );
  }, [folders, shown]);
  // the notes in the order the list shows them, those in a closed folder
  // left out
  const ordered = useMemo(
    () =>
      searching ? shown : [
        ...(inFolder.get(null) ?? []),
        ...folders
          .filter(f => expanded.includes(f.id))
          .flatMap(f => inFolder.get(f.id) ?? []),
      ],
    [searching, shown, inFolder, folders, expanded],
  );

  // Ctrl+Alt+Up and Down open the note above or below the open one, or the
  // last or first with none open. Caught before the editor, whose own
  // binding for the keys adds a cursor.
  const step = (by: 1 | -1) => {
    const at = ordered.findIndex(n => n.id === selectedId);
    const next = ordered[
      at === -1 ? (by === 1 ? 0 : ordered.length - 1) : at + by
    ];
    if (next) select(next.id);
  };
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey || e.shiftKey || e.metaKey) return;
      const by = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
      if (!by) return;
      e.preventDefault();
      e.stopPropagation();
      stepRef.current(by);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // the open note's row is kept in view as notes are stepped through
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    listRef.current?.querySelector('li.selected')
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const noteRow = (note: NoteSummary, folderId: string | null) => (
    <li
      key={note.id}
      className={classNames({
        selected: note.id === selectedId,
        archived: note.archived,
        trashed: note.trashed,
        'in-folder': folderId && !searching,
        'drop-target': folderId && dropTarget === folderId,
      })}
      draggable={!searching}
      onDragStart={e => {
        dragged.current = note.id;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', titleOf(note));
      }}
      onDragEnd={() => {
        dragged.current = null;
        setDropTarget(undefined);
      }}
      {...(folderId ? dropInto(folderId) : {})}
      onClick={() => select(note.id)}>
      <span className="row-text">
        <span className={classNames('title', { unsynced: note.dirty })}>
          {note.id === selectedId
            ? titleOf({
              title: draft.title,
              preview: previewOf(draft.body),
            })
            : titleOf(note)}
        </span>
        {note.trashed
          ? <span className="tag">Deleted</span>
          : note.archived && <span className="tag">Archived</span>}
      </span>
      {note.pinned && (
        <span className="pin-mark" title="Pinned">
          <PinIcon />
        </span>
      )}
      {menuFor(note)}
    </li>
  );

  const folderRows = (folder: NoteSummary) => {
    const open = expanded.includes(folder.id);
    const inside = inFolder.get(folder.id) ?? [];
    const FolderIcon = open ? FolderOpen : Folder;
    return [
      <li
        key={folder.id}
        className={classNames('folder', {
          open,
          'drop-target': dropTarget === folder.id,
        })}
        aria-expanded={open}
        {...dropInto(folder.id)}
        onClick={() => setExpanded(folder.id, !open)}>
        <ChevronRight className="chevron" aria-hidden="true" />
        <FolderIcon className="folder-icon" aria-hidden="true" />
        <span className="row-text">
          <span className={classNames('title', { unsynced: folder.dirty })}>
            {folder.title || 'Untitled'}
          </span>
          <span className="count">{inside.length}</span>
        </span>
      </li>,
      ...(open ? inside.map(n => noteRow(n, folder.id)) : []),
    ];
  };

  return (
    <div
      className={classNames('notes', { 'has-selection': selected })}
      style={{ '--sidebar-width': `${sidebarWidth}rem` } as CSSProperties}>
      <aside className="sidebar">
        <div className="search-row">
          <input
            className="search"
            type="search"
            placeholder="Search"
            aria-label="Search notes"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onKeyDown={e => {
              if (e.key === 'Escape') setQuery('');
            }} />
          <SearchFilter
            showArchived={showArchived}
            onShowArchived={setShowArchived}
            showDeleted={showDeleted}
            onShowDeleted={setShowDeleted} />
        </div>
        <ul
          ref={listRef}
          {...dropInto(null)}
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setDropTarget(undefined);
            }
          }}>
          {searching
            ? shown.map(note => noteRow(note, null))
            : [
              ...(inFolder.get(null) ?? []).map(note => noteRow(note, null)),
              ...folders.flatMap(folderRows),
            ]}
          {searching && !shown.length
            && <li className="muted">No matches</li>}
        </ul>
        <footer>
          <SyncBadge status={status} onClick={syncNow} />
          <SettingsMenu
            notes={allNotes}
            onNewFolder={createFolder}
            onSignOut={async () => {
              // the note being typed is kept before the notes go away
              await flush();
              onSignOut();
            }} />
          <button
            className="new-note"
            title="New note"
            aria-label="New note"
            onClick={create}>
            <SquarePen aria-hidden="true" />
          </button>
        </footer>
        <SidebarResizer width={sidebarWidth} onWidth={setSidebarWidth} />
      </aside>
      <SyncError status={status} onRetry={syncNow} />
      {status.state !== 'failed' && unreadable.length > 0 && (
        <Unreadable ids={unreadable} />
      )}
      {updateReady && (
        <UpdateReady
          onReload={async () => {
            await flush();
            location.reload();
          }} />
      )}
      {selected
        ? (
          // keyed so a newly opened note gets its own textarea, never the
          // previous note's
          <main
            className="editor"
            key={selected.id}
            style={{ '--note-width': `${selected.width}%` } as CSSProperties}
            onKeyDown={e => {
              // the browser's own find misses what the editor hasn't drawn
              if (e.key !== 'f' || !(e.ctrlKey || e.metaKey) || e.altKey) {
                return;
              }
              e.preventDefault();
              bodyRef.current?.find();
            }}>
            <header>
              <button
                className="back"
                title="Notes"
                aria-label="Notes"
                onClick={() => show(null)}>
                <ChevronLeft aria-hidden="true" />
              </button>
              <input
                className={classNames('note-title', {
                  unsynced: selected.dirty,
                })}
                placeholder="Title"
                aria-label="Title"
                value={draft.title}
                onChange={e => edit(selected.id, { title: e.target.value })}
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  bodyRef.current?.focus();
                }}
                autoFocus={!draft.title && !draft.body} />
              {menuFor(selected, {
                insertTable: () => bodyRef.current?.insertSheet(),
                insertRecipe: () => bodyRef.current?.insertRecipe(),
                insertCalc: () => bodyRef.current?.insertCalc(),
                insertChecklist: () => bodyRef.current?.insertChecklist(),
                checklistDone: () => bodyRef.current?.checklistDone() ?? null,
                checkAll: checked => bodyRef.current?.checkAll(checked),
                insertDateTime: () => bodyRef.current?.insertDateTime(),
                find: () => bodyRef.current?.find(),
              })}
            </header>
            <NoteEditor
              ref={bodyRef}
              value={draft.body}
              onChange={body => edit(selected.id, { body })}
              // on a phone focus would bring the keyboard up over the note;
              // it waits for a tap there
              autoFocus={Boolean(draft.title || draft.body) && !isMobile()} />
          </main>
        )
        : (
          <main className="editor empty">
            <span>No note selected</span>
            <span className="hint">
              Pick one from the list, or start a new one
            </span>
          </main>
        )}
    </div>
  );
};

const PinIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M9.5 1.5 14.5 6.5l-1.4 1.4-.7-.7-2.8 2.8.4 3.2-1.4 1.4L5.5 11.5 2 15l-1-1 3.5-3.5-3.1-3.1 1.4-1.4 3.2.4 2.8-2.8-.7-.7z" />
  </svg>
);

// the badge is easy to miss, and hidden on a phone while a note is open;
// a failed sync means edits aren't leaving the device, so it says so up top,
// until dismissed for the rest of the session; the badge still shows it
const SyncError = (
  { status, onRetry }: { status: SyncStatus, onRetry: () => void },
) => {
  const [dismissed, setDismissed] = useState(false);
  if (status.state !== 'failed' || dismissed) return null;
  return (
    <div className="sync-error" role="alert">
      <span className="message">
        <strong>Sync failed</strong> - changes are only on this device.{' '}
        <span className="detail">{status.error}</span>
        {status.stalled && (
          <span className="detail">
            {' '}GitHub keeps not answering this browser: its connection is
            stuck, as it can be after an upload stalls. Switching the network
            off and on clears it, or it clears by itself in a few minutes.
          </span>
        )}
      </span>
      <button onClick={onRetry}>Retry</button>
      <button
        className="dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}>
        ×
      </button>
    </div>
  );
};

// Notes on the remote this passphrase can't read, left untouched there: one
// new to this device stays out of the list, one it had keeps the version it
// had. Dismissed, it stays away for the rest of the session unless
// another note turns up unreadable.
const Unreadable = ({ ids }: { ids: string[] }) => {
  const [dismissed, setDismissed] = useState<string[]>([]);
  if (ids.every(id => dismissed.includes(id))) return null;
  return (
    <div className="unreadable-notes" role="alert">
      <span className="message">
        <strong>
          {ids.length === 1
            ? 'A note on the remote can\'t be read'
            : `${ids.length} notes on the remote can't be read`}
        </strong>{' '}
        - written with another passphrase, or damaged. They are left as they
        are.{' '}
        <span className="detail">
          {ids.map(id => `notes/${id}.enc`).join(', ')}
        </span>
      </span>
      <button
        className="dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(ids)}>
        ×
      </button>
    </div>
  );
};

// a new build is in use from the next launch (../sw.ts); until then this
// page runs the old one
const UpdateReady = ({ onReload }: { onReload: () => void }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="update-ready" role="status">
      <span className="message">A new version is ready.</span>
      <button onClick={onReload}>Reload</button>
      <button
        className="dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}>
        ×
      </button>
    </div>
  );
};

const SyncBadge = (
  { status, onClick }: { status: SyncStatus, onClick: () => void },
) => (
  <button
    className={classNames('sync', status.state)}
    onClick={onClick}
    title={status.state === 'failed' ? status.error : 'Sync now'}>
    {status.state === 'syncing'
      ? status.progress
        ? `Syncing ${status.progress.done}/${status.progress.total}…`
        : 'Syncing…'
      : status.state === 'failed'
      ? 'Sync failed'
      : status.lastSynced
      ? `Synced ${
        status.lastSynced.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        })
      }`
      : 'Not synced'}
  </button>
);
